import { useAnalytics } from '@/hooks/useAnalytics.js';
import useUser from '@/hooks/useUser.js';
import { getG2ReviewState } from '@/redux/actions/gravityActions.js';
import selectors from '@/redux/selectors/selectors.js';
import companyActions from '@/screens/workflows/redux/actions/companyActions.js';
import { useEffect, useMemo, useState } from 'react';
import { PiHandsClappingDuotone } from 'react-icons/pi';
import { useSelector } from 'react-redux';
import Button from './Button.jsx';
import { FiMinus } from './react-icons/main';
import IconV2 from './v2/IconV2.jsx';
import { setModalState } from '@/redux/actions/uiActions/modalActions.js';
import G2ReviewModal from './G2ReviewModal.jsx';

const G2ReviewPrompt = () => {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(null);
  const { jwt } = useUser();
  const { trackEvent } = useAnalytics();
  const admin = useSelector(selectors.admin);
  const activeCompany = useSelector(selectors.activeCompany);
  const showG2Reviews = activeCompany?.settings?.showG2Reviews;
  const g2Reviews = activeCompany?.settings?.g2Reviews;

  const userId = jwt?.id;

  const show = useMemo(() => {
    if (!showG2Reviews || admin || error) {
      return false;
    }

    if (!g2Reviews || !g2Reviews?.[userId]) {
      return true;
    }

    const userReviewData = g2Reviews[userId];
    if (userReviewData === true) {
      return false;
    }

    if (userReviewData instanceof Date) {
      return new Date() > userReviewData;
    }

    if (typeof userReviewData === 'string') {
      const dismissDate = new Date(userReviewData);
      return new Date() > dismissDate;
    }

    return true;
  }, [admin, g2Reviews, showG2Reviews, userId, error]);

  const handleClose = () => {
    const oneWeekFromNow = new Date();
    oneWeekFromNow.setDate(oneWeekFromNow.getDate() + 7);
    companyActions.set('settings.g2Reviews', { ...(g2Reviews || {}), [userId]: oneWeekFromNow.toISOString() });
    trackEvent('g2-review-prompt-dismissed');
  };

  const handleReviewClick = () => {
    setModalState('g2Review', { open: true, url });
    trackEvent('g2-review-prompt-leave-review-clicked');
  };

  useEffect(() => {
    if (userId && show) {
      getG2ReviewState((data, error) => {
        if (error?.success === false) {
          setError(error?.error);
          return;
        }

        if (data?.url) {
          setUrl(data.url);
        } else if (data?.reviewExists) {
          companyActions.set('settings.g2Reviews', { ...(g2Reviews || {}), [userId]: true });
        }
      });
    }
  }, [userId, show]);

  if (!show || !url) {
    return null;
  }

  return (
    <>
      <div className="comp-g2-review-prompt">
        <div className="content">
          <div className="text-row">
            <IconV2 icon={PiHandsClappingDuotone} />
            <div className="text">Enjoying MindCloud?</div>
          </div>
          <Button onClick={handleReviewClick} className="primary full-width">
            Rate your experience
          </Button>
        </div>
        <Button size="xs" icon={FiMinus} onClick={handleClose} className="ghost close-button" />
      </div>
      <G2ReviewModal />
    </>
  );
};

export default G2ReviewPrompt;
